import React, { useState } from "react";
import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import Container from "@/components/ui/Container";
import { ArrowRight, Check } from "lucide-react";
import { Link } from "react-router-dom";
import { usePageTitle } from "@/hooks/usePageTitle";

const ServicesPage = () => {
  usePageTitle("Услуги");
  const [activeTab, setActiveTab] = useState("web-dev");

  const services = [
    {
      id: "web-dev",
      name: "Разработка веб-приложений",
      description: "Создаем современные, быстрые и масштабируемые веб-приложения для решения бизнес-задач любой сложности.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="m18 16 4-4-4-4"></path>
          <path d="m6 8-4 4 4 4"></path>
          <path d="m14.5 4-5 16"></path>
        </svg>
      ),
      features: [
        "Современный стек технологий (React, Vue, Node.js)",
        "Адаптивный дизайн для всех устройств",
        "Интеграция с внешними API и сервисами",
        "Высокая производительность и масштабируемость",
        "SEO-оптимизация"
      ],
      process: [
        "Анализ требований и проектирование",
        "UX/UI дизайн интерфейса",
        "Разработка и интеграция",
        "Тестирование и отладка",
        "Развертывание и поддержка"
      ]
    },
    {
      id: "mobile-dev",
      name: "Разработка мобильных приложений",
      description: "Разрабатываем нативные и кроссплатформенные мобильные приложения для iOS и Android, используя передовые технологии.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect width="14" height="20" x="5" y="2" rx="2" ry="2"></rect>
          <path d="M12 18h.01"></path>
        </svg>
      ),
      features: [
        "Нативная разработка для iOS и Android",
        "Кроссплатформенные решения (React Native, Flutter)",
        "Интеграция с устройствами и системными API",
        "Оффлайн-режим работы",
        "Push-уведомления и геолокация"
      ],
      process: [
        "Определение целей и функциональности",
        "Прототипирование и дизайн интерфейса",
        "Разработка и тестирование",
        "Публикация в App Store и Google Play",
        "Поддержка и обновление"
      ]
    },
    {
      id: "ecommerce",
      name: "Разработка e-commerce решений",
      description: "Создаем и интегрируем современные решения для электронной коммерции, помогающие увеличить продажи и улучшить обслуживание клиентов.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="8" cy="21" r="1"></circle>
          <circle cx="19" cy="21" r="1"></circle>
          <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
        </svg>
      ),
      features: [
        "Индивидуальные интернет-магазины",
        "Интеграция с платежными системами",
        "Управление товарами и категориями",
        "Системы аналитики и отчетности",
        "Программы лояльности и промо-акции"
      ],
      process: [
        "Анализ бизнес-требований",
        "Проектирование архитектуры",
        "Разработка и интеграция",
        "Тестирование и оптимизация",
        "Запуск и техническая поддержка"
      ]
    },
    {
      id: "integration",
      name: "Интеграция систем",
      description: "Обеспечиваем бесшовную интеграцию различных систем и приложений для создания единой работающей экосистемы вашего бизнеса.",
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 2v7"></path>
          <path d="M12 2v4"></path>
          <path d="M21 13v6"></path>
          <path d="M12 17v4"></path>
          <path d="M20 2h-6a2 2 0 0 0-2 2v12.5"></path>
          <path d="M17 5h-1a2 2 0 0 0-2 2v1.5"></path>
          <path d="M10 2H7a2 2 0 0 0-2 2v17a2 2 0 0 0 2 2h7"></path>
          <path d="M22 19H10"></path>
        </svg>
      ),
      features: [
        "Интеграция с 1С и другими ERP-системами",
        "API-интеграции с внешними сервисами",
        "Синхронизация данных между системами",
        "Миграция данных из устаревших систем",
        "ETL-процессы и автоматизация"
      ],
      process: [
        "Аудит существующих систем",
        "Разработка стратегии интеграции",
        "Создание интеграционных решений",
        "Тестирование и отладка",
        "Мониторинг и поддержка"
      ]
    }
  ];

  const technologies = {
    frontend: [
      { name: "React", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
      { name: "Vue.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/vuejs/vuejs-original.svg" },
      { name: "TypeScript", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" },
      { name: "Next.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" },
    ],
    backend: [
      { name: "Node.js", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" },
      { name: "Python", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" },
      { name: "PHP", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/php/php-original.svg" },
      { name: "Go", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg" },
      { name: "1C", icon: "https://www.logo.wine/a/logo/1C_Company/1C_Company-Logo.wine.svg"}
    ],
    database: [
      { name: "PostgreSQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg" },
      { name: "MongoDB", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg" },
      { name: "MySQL", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg" },
      { name: "Redis", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/redis/redis-original.svg" },
    ],
    mobile: [
      { name: "React Native", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg" },
      { name: "Flutter", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flutter/flutter-original.svg" },
      { name: "Swift", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/swift/swift-original.svg" },
      { name: "Kotlin", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kotlin/kotlin-original.svg" },
    ],
    devops: [
      { name: "Docker", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg" },
      { name: "Kubernetes", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/kubernetes/kubernetes-plain.svg" },
      { name: "AWS", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
      { name: "Azure", icon: "https://cdn.jsdelivr.net/gh/devicons/devicon/icons/azure/azure-original.svg" },
    ]
  };

  const activeService = services.find(service => service.id === activeTab) || services[0];

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 bg-secondary">
          <Container>
            <div className="max-w-3xl mx-auto text-center">
              <div className="tag mb-6 animate-fade-in">Наши услуги</div>
              <h1 className="heading text-4xl md:text-5xl font-bold mb-6 animate-fade-in" style={{ animationDelay: "0.2s" }}>
                Комплексные <span className="text-gradient">услуги разработки</span> для вашего бизнеса
              </h1>
              <p className="text-muted-foreground text-lg mb-0 animate-fade-in" style={{ animationDelay: "0.4s" }}>
                Мы предлагаем полный спектр услуг по разработке программного обеспечения для бизнеса. Наша команда экспертов поможет вам реализовать проекты любой сложности.
              </p>
            </div>
          </Container>
        </section>

        {/* Services Section */}
        <section className="py-20">
          <Container>
            {/* Services Tabs */}
            <div className="flex overflow-x-auto pb-4 mb-12 -mx-4 px-4 sm:justify-center sm:pb-0">
              <div className="flex space-x-2">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => setActiveTab(service.id)}
                    className={`px-4 py-2 rounded-full whitespace-nowrap transition-colors ${
                      activeTab === service.id
                        ? "bg-primary text-white"
                        : "bg-secondary text-foreground hover:bg-primary/10"
                    }`}
                  >
                    {service.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Selected Service Info */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              <div>
                <div className="flex items-center mb-6">
                  <div className="w-12 h-12 bg-primary/10 flex items-center justify-center rounded-lg mr-4 text-primary">
                    {activeService.icon}
                  </div>
                  <h2 className="heading text-3xl font-bold">{activeService.name}</h2>
                </div>
                <p className="text-muted-foreground text-lg mb-8">
                  {activeService.description}
                </p>
                
                <h3 className="text-xl font-semibold mb-4">Ключевые особенности</h3>
                <div className="space-y-3 mb-8">
                  {activeService.features.map((feature, index) => (
                    <div key={index} className="flex items-start">
                      <Check className="h-5 w-5 text-primary mr-3 mt-0.5" />
                      <p className="text-foreground/80">{feature}</p>
                    </div>
                  ))}
                </div>
                
                <Link to="/contact" className="button-primary">
                  Заказать консультацию
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </div>
              
              <div>
                <div className="glass rounded-2xl p-6">
                  <h3 className="text-xl font-semibold mb-6">Как мы работаем</h3>
                  <div className="space-y-6">
                    {activeService.process.map((step, index) => (
                      <div key={index} className="flex items-start">
                        <div className="w-8 h-8 bg-primary text-white rounded-full flex items-center justify-center mr-4 shrink-0">
                          {index + 1}
                        </div>
                        <div>
                          <p className="font-medium">{step}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div className="mt-8 bg-white rounded-2xl p-6 shadow-sm border border-border">
                  <h3 className="text-xl font-semibold mb-4">Почему выбирают нас</h3>
                  <ul className="space-y-3">
                    <li className="flex items-start">
                      <div className="mr-3 mt-1 text-primary">•</div>
                      <p className="text-muted-foreground">Опыт разработки более 7 лет</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-3 mt-1 text-primary">•</div>
                      <p className="text-muted-foreground">Команда сертифицированных разработчиков</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-3 mt-1 text-primary">•</div>
                      <p className="text-muted-foreground">Соблюдение сроков и бюджета проекта</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-3 mt-1 text-primary">•</div>
                      <p className="text-muted-foreground">Гибкая методология разработки</p>
                    </li>
                    <li className="flex items-start">
                      <div className="mr-3 mt-1 text-primary">•</div>
                      <p className="text-muted-foreground">Поддержка и сопровождение после запуска</p>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </Container>
        </section>

        {/* Technologies Section */}
        <section className="py-20">
          <Container>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="tag mb-4 mx-auto inline-block">Наш технологический стек</div>
              <h2 className="heading text-3xl font-bold mb-6">
                Технологии, с которыми мы работаем
              </h2>
              <p className="text-muted-foreground">
                Наша команда имеет опыт работы с широким спектром современных технологий, что позволяет нам выбирать оптимальные инструменты для каждого проекта.
              </p>
            </div>

            <div className="space-y-12">
              {Object.entries(technologies).map(([category, techs]) => (
                <div key={category} className="mb-10">
                  <h3 className="text-xl font-semibold mb-6 capitalize">
                    {category === "frontend" ? "Frontend" : 
                     category === "backend" ? "Backend" : 
                     category === "database" ? "Базы данных" : 
                     category === "mobile" ? "Мобильная разработка" : 
                     "DevOps и инфраструктура"}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                    {techs.map((tech) => (
                      <div key={tech.name} className="flex flex-col items-center p-4 bg-white rounded-lg border border-border hover:border-primary/20 hover:shadow-md transition-all">
                        <img 
                          src={tech.icon} 
                          alt={tech.name} 
                          className="w-12 h-12 mb-3"
                          onError={(e) => {
                            e.currentTarget.src = "https://placehold.co/48x48?text=" + tech.name.charAt(0);
                          }}
                        />
                        <span className="text-sm font-medium">{tech.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </Container>
        </section>

        {/* Process Section */}
        <section className="py-20 bg-secondary">
          <Container>
            <div className="text-center max-w-3xl mx-auto mb-16">
              <div className="tag mb-4 mx-auto inline-block">Наш процесс</div>
              <h2 className="heading text-3xl font-bold mb-6">
                Как мы реализуем ваш проект
              </h2>
              <p className="text-muted-foreground">
                Мы следуем структурированному подходу к разработке, который обеспечивает создание высококачественных продуктов в срок и в рамках бюджета.
              </p>
            </div>

            <div className="relative">
              <div className="absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-border hidden md:block"></div>
              
              <div className="space-y-12">
                {[
                  {
                    step: 1,
                    title: "Анализ и планирование",
                    description: "Мы начинаем с глубокого анализа ваших потребностей, целей и ограничений. На этом этапе мы определяем объем работ, составляем план проекта и выбираем оптимальные технологии."
                  },
                  {
                    step: 2,
                    title: "Дизайн и прототипирование",
                    description: "Разрабатываем прототипы интерфейса, создаем пользовательские сценарии и проектируем архитектуру будущего решения, чтобы убедиться, что оно будет интуитивно понятным и удобным для пользователей."
                  },
                  {
                    step: 3,
                    title: "Разработка",
                    description: "Команда разработчиков приступает к написанию кода, следуя лучшим практикам и используя современные технологии. Мы регулярно проводим код-ревью и придерживаемся высоких стандартов качества."
                  },
                  {
                    step: 4,
                    title: "Тестирование",
                    description: "Проводим комплексное тестирование, включая функциональное, интеграционное и нагрузочное, чтобы убедиться, что продукт работает стабильно и соответствует всем требованиям."
                  },
                  {
                    step: 5,
                    title: "Запуск и поддержка",
                    description: "После успешного запуска продукта мы обеспечиваем его техническую поддержку, своевременные обновления и оптимизацию в соответствии с изменяющимися потребностями бизнеса."
                  }
                ].map((item, index) => (
                  <div key={index} className="relative">
                    <div className={`flex flex-col md:flex-row ${index % 2 ? 'md:flex-row-reverse' : ''}`}>
                      <div className="md:w-1/2 flex justify-center mb-8 md:mb-0">
                        <div className="relative">
                          <div className="w-16 h-16 bg-primary text-white rounded-full flex items-center justify-center text-xl font-bold relative z-10">
                            {item.step}
                          </div>
                          <div className="absolute -inset-4 bg-primary/10 rounded-full blur-lg -z-10"></div>
                        </div>
                      </div>
                      <div className={`md:w-1/2 ${index % 2 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                        <h3 className="text-2xl font-semibold mb-3">{item.title}</h3>
                        <p className="text-muted-foreground">{item.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </Container>
        </section>

        {/* CTA Section */}
        <section className="py-20">
          <Container>
            <div className="glass rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-center">
                <div className="lg:col-span-3">
                  <h2 className="heading text-3xl font-bold mb-4">
                    Готовы обсудить ваш проект?
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    Свяжитесь с нами, чтобы получить консультацию эксперта и узнать, как мы можем помочь вашему бизнесу с помощью инновационных технологий.
                  </p>
                  <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                    <Link to="/contact" className="button-primary">
                      Связаться с нами
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                    {/* <Link to="/clients" className="button-secondary">
                      Посмотреть кейсы
                    </Link> */}
                  </div>
                </div>
                <div className="lg:col-span-2 relative">
                  <div className="h-auto max-w-xs mx-auto bg-white/5 rounded-2xl overflow-hidden p-6">
                    <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1531973576160-7125cd663d86?q=80&w=2070')] bg-cover bg-center opacity-10 rounded-2xl"></div>
                    <div className="relative h-full flex flex-col items-center justify-center text-center space-y-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary">
                          <path d="M14 9a2 2 0 0 1-2 2H6l-4 4V4c0-1.1.9-2 2-2h8a2 2 0 0 1 2 2v5Z"></path>
                          <path d="M18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1"></path>
                        </svg>
                      </div>
                      <h3 className="text-2xl font-semibold">Бесплатная консультация</h3>
                      <p className="text-muted-foreground">
                        Мы предлагаем бесплатную первичную консультацию, чтобы понять ваши потребности и предложить оптимальное решение.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
